import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import { Button, ErrorAlert, Modal, Spinner } from "@/components/ui";
import { portal } from "@/lib/api";
import { formatDate, formatMoney } from "@/lib/format";
import type { BillingPeriod, Charge, CustomerAccount, PlanKey } from "@/lib/types";

/**
 * The review step before a plan change: when it applies and exactly what is charged. Nothing
 * changes until "Confirm". (Placeholder for the checkout design; payment collection plugs in here.)
 */
export function ConfirmPlanChangeModal({
  target,
  onClose,
  onDone,
  provider,
}: {
  target: { plan: PlanKey; period: BillingPeriod } | null;
  onClose: () => void;
  onDone: (account: CustomerAccount) => void;
  /** Who processes the charge (the subscription's `payment_provider`). */
  provider?: string;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: ["plan-preview", orgId, target?.plan, target?.period],
    queryFn: () => portal.previewPlanChange(orgId, target!.plan, target!.period),
    enabled: target !== null,
  });
  const confirm = useMutation({
    mutationFn: () => portal.changePlan(orgId, target!.plan, target!.period),
    onSuccess: async (account) => {
      await qc.invalidateQueries({ queryKey: ["subscription", orgId] });
      await qc.invalidateQueries({ queryKey: ["overview", orgId] });
      onDone(account);
    },
  });
  const p = preview.data;
  const cancelling = p?.plan.key === "free";
  const title = !p ? "Review the change" : cancelling ? "Cancel your plan?" : `Move to ${p.plan.name}`;

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {cancelling ? "Keep my plan" : "Back"}
          </Button>
          <Button
            variant={cancelling ? "danger" : "primary"}
            loading={confirm.isPending}
            disabled={!p || p.timing === "none"}
            onClick={() => confirm.mutate()}
          >
            {cancelling
              ? "Cancel plan"
              : p?.due_now
                ? `Confirm and pay ${money(p.due_now.total_cents, p.due_now)}`
                : "Confirm"}
          </Button>
        </>
      }
    >
      <ErrorAlert error={preview.error ?? confirm.error} />
      {preview.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : p ? (
        <div className="space-y-4 text-sm">
          {p.timing === "none" && <p className="text-fg-muted">You're already on this plan.</p>}
          {p.timing === "now" && (
            <p className="text-fg-muted">
              <strong className="text-fg">{p.plan.name}</strong> starts today and its limits apply straight
              away.
            </p>
          )}
          {p.timing === "period_end" && (
            <p className="text-fg-muted">
              {cancelling ? (
                <>
                  Nothing is cancelled yet: your current plan runs until{" "}
                  <strong className="text-fg">{formatDate(p.effective_at)}</strong>, then you move to Free.
                  Custom domains stop working on Free. You can undo this until then.
                </>
              ) : (
                <>
                  You keep your current plan until{" "}
                  <strong className="text-fg">{formatDate(p.effective_at)}</strong>, the end of the period
                  you've paid for. {p.plan.name} starts then. Nothing is charged today.
                </>
              )}
            </p>
          )}
          {p.due_now && <ChargeLines title="Due today" charge={p.due_now} />}
          {p.due_now && !cancelling && <PoweredByStripe provider={provider} />}
          {p.next_charge && (
            <ChargeLines title={`Then, on ${formatDate(p.next_charge.date)}`} charge={p.next_charge} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}

const money = (cents: number, charge: Pick<Charge, "currency">) => formatMoney(cents, charge.currency);

function ChargeLines({ title, charge }: { title: string; charge: Charge }) {
  return (
    <div className="rounded-xl border border-border bg-brand-50 p-4">
      <p className="mb-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">{title}</p>
      <dl className="space-y-1.5">
        <div className="flex justify-between">
          <dt className="text-fg-muted">{charge.description}</dt>
          <dd>{money(charge.amount_cents, charge)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-fg-muted">VAT</dt>
          <dd>{money(charge.vat_cents, charge)}</dd>
        </div>
        <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
          <dt>Total</dt>
          <dd>{money(charge.total_cents, charge)}</dd>
        </div>
      </dl>
    </div>
  );
}
